import { ReactElement, useContext } from 'react'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { CorneredSection } from '@ui2/structure'
import { Form, UploadField } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useControlStore } from '@editorStores'
import { useUploadIFCFile } from '@editorHooks'
import useIFCImportUpload from '../../hooks/useIFCImportUpload'
import { GuidedIFCContext } from '../GuidedIFCDrawer/context'

interface Props {
  /**
   * Called after the IFC file has finished uploading & converting. Defaults to advancing the
   * guided wizard when inside it, otherwise closing the add panel (used by the "Add" mode).
   */
  onUploaded?: () => void
}

/**
 * IFC file upload shown in the IFC Import drawer — the same content as the IFCUploadModal, but
 * embedded in the sidebar instead of a dialog.
 */
const IFCUploadPanel = ({ onUploaded }: Props): ReactElement => {
  const { closeAdd } = useIFCImportUpload()
  const guided = useContext(GuidedIFCContext)
  const setUploadCompleted = useControlStore(state => state.setIfcGuidedUploadCompleted)
  const { enqueueSnackbar } = useSnackbar()

  const advance = onUploaded ?? guided?.goToNext ?? closeAdd

  // In the guided wizard, mark the upload step done so it is completed & skipped, then advance.
  const handleUploaded = () => {
    if (guided) setUploadCompleted(true)
    advance()
  }

  const { mutate: uploadIFC, isLoading } = useUploadIFCFile(() => handleUploaded())

  const handleSelect = (file: File) => {
    if (file && file.name.toLowerCase().endsWith('.ifc')) {
      uploadIFC(file)
    } else if (file) {
      enqueueSnackbar('Bitte wählen Sie eine IFC-Datei aus', { variant: 'error' })
    }
  }

  return (
    <CorneredSection>
      <Form onSubmit={() => undefined} defaultValues={{ ifcFile: undefined }}>
        <Stack direction="column" spacing={2}>
          <Typography>
            IFC-Datei zum Hochladen &amp; Konvertieren auswählen, umbennenen kann später vorgenommen
            werden.
          </Typography>
          <UploadField
            name="ifcFile"
            accept={{ 'application/octet-stream': ['.ifc'] }}
            onSelect={handleSelect}
            disabled={isLoading}
          />
          {isLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                IFC-Datei wird hochgeladen &amp; konvertiert …
              </Typography>
            </Stack>
          )}
        </Stack>
      </Form>
    </CorneredSection>
  )
}

export default IFCUploadPanel
